import { useState, useEffect, useCallback, type ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { FilterDropDownType, UserRoles } from "@/utils/utils";
import CustomFilterDropdown from "@/components/CustomFilterDropdown/CustomFilterDropdown";
import "./ConnectionGrid.css";
import { Typography, Box, TextField } from "@mui/material";
import {
  selectConnectionSummaryRecords,
  fetchConnectionSummaries,
  selectConnectionSummaryRecordLoaded,
  setConnectionSummaryFilterTypes,
  setConnectionSummaryFilterStatus,
  selectConnectionSummaryFilterTypes,
  selectConnectionSummaryFilterStatus,
  connEndpointStatus,
  connectionTypes,
  OperationProcessState,
  setSelectedConnection,
  selectTriggerRefreshForConnectionSummary,
  setEditConnectionOpen,
  setEditConnectionId,
  type IConnectionSummaryRecord,
  type IConnectionType,
  type IFetchConnectionSummariesAction,
  selectConnectionErrorRates,
} from "@/features/connections/integrationSlice";
import ShepTooltip from "@/components/GenericComponents/ShepTooltip";
import ShepHorizontalMenu from "@/components/GenericComponents/ShepHorizontalMenu";
import ThreeDotSettingMenu from "./ThreeDotSettingMenu";
import _ from "lodash";
import AddConnectionPopup from "@/components/Integrations/ManageConnection/AddConnection/AddConnectionPopup";
import axios from "../../features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import ShepTable, { type IColumnVisibilityModel } from "@/components/GenericComponents/ShepTable";
import { fetchUserRole, selectUserRole } from "@/features/auth/userSlice";
import { useAppDispatch } from "@/typedDispatch";
import type { GridRenderCellParams } from "@mui/x-data-grid";

const getType = (type: number) => {
  return loadLabel(type, connectionTypes);
};

const loadLabel = (key: number, collection: IConnectionType[]) => {
  if (!collection) return "";
  const items = collection.filter((x) => x.key === key);
  if (items && items.length) {
    return items[0].label;
  }
};
const getStatusClass = (status: number) => {
  switch (status) {
    case OperationProcessState.Completed:
      return "connection-summary-status-success";
    case OperationProcessState.CompletedWithWarning:
      return "connection-summary-status-success";
    case null:
      return "";
    default:
      return "connection-summary-status-error";
  }
};

interface IConnectionGrid {
    displayType: string;
    searchText: string;
    title: string;
    canSelect: boolean;
    columnVisibilityModel: IColumnVisibilityModel;
    hasGridActions?: boolean;
}
const ConnectionGrid = (props : IConnectionGrid) => {
  const dispatch = useAppDispatch();
  const [referenceFilter, setReferenceFilter] = useState("");
  const [selectedRows, setSelectedRows] = useState<IConnectionSummaryRecord[]>([]);
  const [addConnectionOpen, setAddConnectionOpen] = useState(false);
  const statusFilter = useSelector(selectConnectionSummaryFilterStatus);
  const typeFilter = useSelector(selectConnectionSummaryFilterTypes);
  const recordLoaded = useSelector(selectConnectionSummaryRecordLoaded);
  const rows = useSelector(selectConnectionSummaryRecords);

  const errorRates = useSelector(selectConnectionErrorRates);

  const triggerRefresh = useSelector(selectTriggerRefreshForConnectionSummary);
  const userRole = useSelector(selectUserRole);
  const columns = [
    {
      field: "DisplayName",
      headerName: "Connection",
      width: 230,
    },
    {
      field: "ConnectionType",
      headerName: "Type",
      width: 180,
      renderCell: (params: GridRenderCellParams<IConnectionSummaryRecord>) => {
        return getType(params.row.ConnectionType);
      },
    },
    {
      field: "EndpointName",
      headerName: "Endpoint",
      width: 220,
    },
    {
      field: "Status",
      headerName: "Status",
      width: 150,
      renderCell: (params: GridRenderCellParams<IConnectionSummaryRecord>) => {
        const statusClass = getStatusClass(params.row.Status);
        return (
          <ShepTooltip
            title={
              params.value < OperationProcessState.FailedWithError
                ? loadLabel(OperationProcessState.Completed, connEndpointStatus)
                : loadLabel(
                    OperationProcessState.FailedWithError,
                    connEndpointStatus,
                  )
            }
          >
            <div className={statusClass}></div>
          </ShepTooltip>
        );
      },
    },
    {
      field: "ConnectionId",
      headerName: "Error Rate (Last 100 Attempts)",
      width: 320,
      renderCell: (params: GridRenderCellParams<IConnectionSummaryRecord>) => {
        let message = "";
        const connectionId = params.row.ConnectionId;
        if (!errorRates[connectionId]) {
          return <Typography></Typography>;
        }
        const {
          FailedAttempts: failed,
          TotalAttempts: total,
        } = errorRates[connectionId];

        if (total && total > 0) {
          message =
            failed +
            " of " +
            total +
            " (" +
            Math.round((failed * 100) / total) +
            "%) attempts  failed   ";
        }
        return <Typography>{message}</Typography>;
      },
    },
    {
      field: "SchemaCollectionName",
      headerName: "Schema Library ID",
      width: 300,
    },
    {
      field: "LogTimeUTC",
      headerName: "Last Update",
      width: 200,
      flex: 1,
      type: "date",
      valueGetter: (params: GridRenderCellParams<IConnectionSummaryRecord>) => (params.row.LogTimeUTC ? new Date(params.row.LogTimeUTC) : null),
      renderCell: (params: GridRenderCellParams<IConnectionSummaryRecord>) => {
        return <Typography>{params.row.LastUpdated}</Typography>;
      },
    },
    {
      field: "threeDotMenu",
      type: "actions",
      sortable: false,
      renderCell: (params: GridRenderCellParams<IConnectionSummaryRecord>) => {
          return <ThreeDotSettingMenu id={params.row.id} selectedRow={params.row} />;
      },
    },
  ];

  useEffect(() => {
    prepFilters();
    dispatch(fetchUserRole());
  }, []);

  const getStatusFilterString = () => {
    const status = statusFilter;

    return status
      ? JSON.stringify(status.filter((x) => x.isChecked).map((x) => x.key))
      : "";
  };

  const getTypeFilterString = () => {
    const types = typeFilter;

    return types
      ? JSON.stringify(types.filter((x) => x.isChecked).map((x) => x.key))
      : "";
  };


  useEffect(() => {
    const params = {
      statusFilterJson: getStatusFilterString(),
      typeFilterJson: getTypeFilterString(),
      search: referenceFilter,
      displayType: props.displayType
    };
      debouncedGetConnectionSummary(params);
  }, [
    typeFilter,
    statusFilter,
    referenceFilter,
    triggerRefresh,
  ]);

  const getConnectionsFromServer = (params: IFetchConnectionSummariesAction) => {
    dispatch(fetchConnectionSummaries(params));
  };
  const debouncedGetConnectionSummary = useCallback(
    _.debounce(getConnectionsFromServer, 1000),
    [],
  );
  const prepFilters = () => {
    dispatch(setConnectionSummaryFilterTypes({ value: connectionTypes }));
    dispatch(setConnectionSummaryFilterStatus({ value: connEndpointStatus }));
  };
  const schema = [
    {
      id: 0,
      label: "Status",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Status",
      setValue: setConnectionSummaryFilterStatus,
      readVal: selectConnectionSummaryFilterStatus,
      labelKey: "label",
      uniqueKey: "key",
    },
    {
      id: 1,
      label: "Connection Type",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Connection Type",
      setValue: setConnectionSummaryFilterTypes,
      readVal: selectConnectionSummaryFilterTypes,
      labelKey: "label",
      uniqueKey: "key",
    },
  ];

  interface IConnectionAction {
    label: string;
    onClick: () => void;
    variant: string | null;
}
  const deleteAction: IConnectionAction = {
      label: "Delete",
      onClick: () => handleConnectionDelete(),
      variant: "danger",
    };
  const connectionActions: IConnectionAction[] = (
    userRole === UserRoles.SuperAdmin
      ? [
          {
            label: "Add Connection",
            onClick: () => {
              setAddConnectionOpen(true);
            },
            variant: null,
          },
        ]
      : []
  );
  connectionActions.push(deleteAction);

  const handleRowClick = (ids: number[]) => {
    const selectedIDs = new Set(ids);
    const selectedRowData = rows.filter((row) => selectedIDs.has(row.id));
    setSelectedRows(selectedRowData);
  };

  const handleConnectionDelete = () => {
      if (!selectedRows?.length) {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Please select at least one connection.",
          }),
        );
        return;
      }
      const selectedRowsToDelete = selectedRows.map((row) => row.ConnectionId);
      deleteRows(selectedRowsToDelete);

      // heap.io
      window.heap.track("Connection Manager - Delete Connection");
  };

  const deleteRows = (selectedRowsToDelete: number[]) => {
    axios
      .delete("/connections/delete-connection-without-connection-pairs", {
        params: {
          connectionIds: JSON.stringify(selectedRowsToDelete),
        },
      })
      .then((response) => {
        if (response?.status === 201) {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message: response?.data?.Message,
            }),
          );
        } else if (response?.data) {
          dispatch(fetchConnectionSummaries(null));
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              message: "Connection Deleted!",
            }),
          );
        } else {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message: "Error deleting the connection",
            }),
          );
        }
      })
      .catch((error) => {
        console.error(error);
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Something went wrong, please try again!",
          }),
        );
      });
  };

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const referenceNumber = event.target.value;
    setReferenceFilter(referenceNumber);
  };

  return (
    <Box sx={{ display: "block", flexDirection: "column" }}>
      <Box style={{ display: "flex", paddingBottom: 5 }}>
        <CustomFilterDropdown schemas={schema} />
        <TextField
          placeholder={props.searchText}
          value={referenceFilter}
          onChange={handleTextChange}
          sx={{
            paddingLeft: "10px",
            width: "450px",
            "&.MuiFormControl-root .MuiInputBase-root": {
              borderRadius: "5px",
            },
            "&:hover fieldset": {
              color: "#000000",
            },
          }}
        />
        {props.hasGridActions && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <ShepHorizontalMenu actions={connectionActions} />
          </div>
        )}
      </Box>
      <Box
        className="content-box"
        sx={{ display: "flex", flexDirection: "row" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
            <ShepTable
              tableTitle={props.title}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'DisplayName', sort: 'asc' }] 
                }
              }}
              isDataLoaded={recordLoaded}
              dataRows={rows}
              columnTitles={columns}
              checkboxesEnabled={props.canSelect}
              selectionDisabled={!props.canSelect}
              handleSelectedRowChange={handleRowClick}
              columnVisibilityModel={props.columnVisibilityModel}
            />
          </Box>
        </Box>
      </Box>
      {addConnectionOpen && (
        <AddConnectionPopup
          open={addConnectionOpen}
          onClose={() => {
            setAddConnectionOpen(false);
            setEditConnectionOpen(false);
            dispatch(setEditConnectionOpen(false));
            dispatch(setSelectedConnection({
              selectedConnectionInfo: {
                ConnectionId: 0,
                ConnectionName: ""
              }
            }));
            dispatch(setEditConnectionId(0));
          }}
        />
      )}
    </Box>
  );
};

export default ConnectionGrid;
